import { Badge } from '@/components/ui/badge';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import type { RouteHealthItem } from '../types';

interface RouteHealthTableProps {
	routes: RouteHealthItem[];
}

function fmtNum(n: number): string {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
	return n.toLocaleString();
}

function healthVariant(
	health: RouteHealthItem['health'],
): 'default' | 'destructive' | 'secondary' | 'outline' {
	if (health === 'ok') return 'default';
	if (health === 'warn') return 'secondary';
	return 'outline';
}

export function RouteHealthTable({ routes }: RouteHealthTableProps) {
	if (routes.length === 0) {
		return (
			<p className="py-8 text-center text-sm text-muted-foreground">
				No routing configurations found for the selected filter.
			</p>
		);
	}

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Route</TableHead>
					<TableHead>Subdomain</TableHead>
					<TableHead>Status</TableHead>
					<TableHead>Success Rate</TableHead>
					<TableHead>Avg Latency</TableHead>
					<TableHead>Sends (7d)</TableHead>
					<TableHead>Last Activity</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{routes.map((r) => {
					const barColor =
						r.successRate >= 95
							? 'bg-green-500'
							: r.successRate >= 85
								? 'bg-amber-500'
								: 'bg-red-500';
					const textColor =
						r.successRate >= 95
							? 'text-green-700'
							: r.successRate >= 85
								? 'text-amber-700'
								: 'text-red-700';

					return (
						<TableRow key={r.name}>
							<TableCell className="font-medium">{r.name}</TableCell>
							<TableCell>
								<Badge variant="outline">{r.subdomain}</Badge>
							</TableCell>
							<TableCell>
								<Badge variant={healthVariant(r.health)}>{r.status}</Badge>
							</TableCell>
							<TableCell>
								<div className="flex items-center gap-2">
									<div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
										<div
											className={`h-full rounded-full ${barColor}`}
											style={{ width: `${r.successRate}%` }}
										/>
									</div>
									<span className={`text-xs ${textColor}`}>
										{r.successRate}%
									</span>
								</div>
							</TableCell>
							<TableCell className="text-muted-foreground">
								{r.avgLatency}
							</TableCell>
							<TableCell>{fmtNum(r.sends7d)}</TableCell>
							<TableCell className="text-muted-foreground">
								{r.lastEvent}
							</TableCell>
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	);
}
